import { AlertColor, Box } from "@mui/material";
import { AxiosError } from "axios";
import { useRef, useState } from "react";
import { useMutation, useQuery } from "react-query";
import {
  createSearchParams,
  Link as RouterLink,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useForm } from "react-hook-form";
import { useReactToPrint } from "react-to-print";
import ParkOutlinedIcon from "@mui/icons-material/ParkOutlined";
// import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined"; // only used by the commented-out Estimated Fund Details card below
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DashboardWrapper from "src/components/common/DashboardWrapper";
import Snackbar from "src/components/common/Snackbar";
import { useAppSelector } from "src/redux/store";
import { getStyles } from "src/styles/theme";
import {
  fundApplicationQuery,
  getFundApplicationById,
  updateFundRequestStatus,
  verifyFundApplication,
} from "src/utils/api/dashboard";
import defaultStyles, { StylesClasses } from "../styles";
import { dateAndTimeStamp } from "src/utils/helper";
import { Button } from "src/components/common/FormComponents/Buttons";
import { Input } from "src/components/common/FormComponents/Input";
import DateInput from "src/components/common/FormComponents/MaskedDate";
import errorMessage from "src/constants/errorMessage";
import FileUpload from "src/components/common/FormComponents/FileUplaod";
import { path } from "src/constants/path";
import {
  ActionFooter,
  ActionNote,
  ActionPanel,
  FUND_SECTIONS,
  FundDetailSkeleton,
  FundLoadError,
  PageProgressBar,
  readApiError,
  useScrollToSection,
  AmountRow,
  ConfirmActionDialog,
  queryButtonStyles,
  FormGrid,
  FormHint,
  PendingAction,
  ApplicationTimeline,
  DetailField,
  DetailFieldGrid,
  DetailLayout,
  DocumentTile,
  hasPreviousUcDetails,
  PreviousUcSummary,
  previousUcFromCurrentFa,
  SectionCard,
  StatusHistoryTrail,
  StatusPill,
  SubSection,
  SubSectionRow,
  UcSubmitPayload,
  UtilizationCertificatePanel,
  fundUi,
  installmentLabel,
  money,
  shortDate,
} from "./FundApplicationUI";
import {
  downloadFundReleaseLetter,
  FundReleaseLetterFormat,
} from "./fundReleaseLetter";

const FundApplicationDetails = () => {
  const styles = getStyles<StylesClasses>(defaultStyles);

  const [searchParams] = useSearchParams();

  const componentRef = useRef();

  const FundApplicationId = searchParams.get("id");

  // Set when the user arrived by clicking an Action in the fund application
  // list — the panel that action belongs to is scrolled to and ringed.
  const targetSection = searchParams.get("section");

  const user = useAppSelector((state) => state.root.user);

  const navigate = useNavigate();

  // Holds the selected format while the letter is being assembled and handed
  // to the browser. Both choices stay disabled so parallel downloads cannot
  // compete for the same in-browser document renderer.
  const [buildingLetterFormat, setBuildingLetterFormat] =
    useState<FundReleaseLetterFormat | null>(null);

  // Every fund action is one-way, so each one is confirmed before it runs.
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(
    null
  );

  // Every action on this page ends in a toast — the outcome used to be left to
  // be inferred from the page quietly re-rendering.
  const [feedback, setFeedback] = useState<{
    message: string;
    severity: AlertColor;
  } | null>(null);

  // Read straight off the query's own `data` rather than copying it into a
  // separate `useState` in `onSuccess` — that copy landed a render behind
  // `isLoading` flipping to false, so the page briefly showed its shell with
  // empty fields before the real data arrived. Deriving it here keeps
  // `projectData` and `isLoading` in the same render.
  const {
    data: fundApplicationResponse,
    isLoading,
    isFetching,
    refetch,
  } = useQuery(
    ["userDetails", FundApplicationId],
    () => getFundApplicationById(FundApplicationId),
    {
      onError: (error: AxiosError) => {
        setFeedback({
          message: readApiError(
            error,
            "Could not load this fund application. Please try again."
          ),
          severity: "error",
        });
      },
      enabled: !!FundApplicationId,
      refetchOnMount: true,
    }
  );

  const projectData = fundApplicationResponse?.data;

  const { mutate, isLoading: isDeciding } = useMutation(
    "verifyFundApplication",
    ({
      FundApplicationId,
      data,
    }: {
      FundApplicationId: string;
      data: {
        status: string;
        remark: string;
        totalAmountReleased?: number;
        paymentApproveDocId?: string;
        releasedDate?: string;
      };
    }) =>
      verifyFundApplication({
        FundApplicationId,
        data,
      }),
    {
      onSuccess: () => {
        setPendingAction(null);
        navigate(path.fundTransfer);
      },
      onError: (error: AxiosError) => {
        setPendingAction(null);
        setFeedback({
          message: readApiError(
            error,
            "Could not record that decision. Please try again."
          ),
          severity: "error",
        });
      },
    }
  );

  // The admin is the approver, so recording the UC here accepts it outright —
  // the same status the "Verify Utilization Certificate" panel patches to
  // when accepting one the SNO uploaded.
  const { mutate: submitUc, isLoading: isSubmittingUc } = useMutation(
    (payload: UcSubmitPayload) =>
      updateFundRequestStatus(FundApplicationId, {
        status: "UC_APPROVED",
        ...payload,
      }),
    {
      // Returning the refetch holds the mutation in its loading state until
      // the fresh data has landed, so the spinner does not stop while the page
      // is still showing the pre-action content.
      onSuccess: async () => {
        await refetch();
        setPendingAction(null);
        setFeedback({
          message: "Utilization Certificate recorded and accepted.",
          severity: "success",
        });
      },
      onError: (error: AxiosError) => {
        setPendingAction(null);
        setFeedback({
          message: readApiError(
            error,
            "Could not record the Utilization Certificate. Please try again."
          ),
          severity: "error",
        });
      },
    }
  );

  const { mutate: mutateQueryData, isLoading: isRaisingQuery } = useMutation(
    "query",
    ({ id, query }: { id: string; query: string }) =>
      fundApplicationQuery({
        id,
        query,
      }),
    {
      onSuccess: async () => {
        await refetch();
        setValue("query", "");
        setPendingAction(null);
        setFeedback({
          message: "Your query has been sent to the SNO.",
          severity: "success",
        });
      },
      onError: (error: AxiosError) => {
        setPendingAction(null);
        setFeedback({
          message: readApiError(
            error,
            "Could not raise the query. Please try again."
          ),
          severity: "error",
        });
      },
    }
  );

  const {
    control,
    watch,
    setValue,
    trigger,
    setError,
    clearErrors,
    getValues,
    formState: { errors, isValid },
  } = useForm<any>({
    mode: "onChange",
    defaultValues: {
      query: "",
      remark: "",
      totalAmountReleased: "",
      paymentApproveDocId: "",
      // Pre-filled to today so this behaves like the old automatic timestamp
      // by default — the admin only needs to touch it to backdate a release.
      releasedDate: new Date().toISOString().slice(0, 10),
    },
  });

  useScrollToSection(targetSection, !isLoading && !!projectData?.fundDetails);

  // The sanction letter is assembled in the browser from the application that
  // is already loaded, so it always carries this FA's own figures — including
  // the amount the admin has just typed, before the release is recorded.
  const handleDownloadReleaseLetter = async (
    format: FundReleaseLetterFormat
  ) => {
    setBuildingLetterFormat(format);
    try {
      const { letterheadEmbedded } = await downloadFundReleaseLetter(
        projectData,
        watch("totalAmountReleased"),
        format
      );

      // The masthead is fetched from the build's own assets, so it can only be
      // missing where those did not reach the server. Said out loud rather than
      // swallowed: an unheaded sanction letter looks fine until it is signed.
      if (!letterheadEmbedded) {
        setFeedback({
          message:
            "The letter downloaded without the ICFRE letterhead — the masthead image could not be loaded. Print it on official letterhead, or report this so the portal's assets can be redeployed.",
          severity: "warning",
        });
      }
    } catch (error) {
      setFeedback({
        message: `Could not build the fund release letter as ${format.toUpperCase()}. Please try again.`,
        severity: "error",
      });
    } finally {
      setBuildingLetterFormat(null);
    }
  };

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    pageStyle: "@media print { body { -webkit-print-color-adjust: exact; } }",
  });

  const docId = getValues("paymentApproveDocId");

  // Only one fund action is ever in flight at a time, so a single flag drives
  // the confirmation dialog's spinner and the action panel overlays.
  const isActionRunning = isDeciding || isRaisingQuery || isSubmittingUc;

  const fundDetails = projectData?.fundDetails;

  const landOwnership = fundDetails?.landOwnership;

  const totalArea = Number(landOwnership?.totalArea);

  const status = fundDetails?.status;

  const isViewOnly = user.data.role === "Approver-view-only";

  // An archived application is served read-only from the archive tables —
  // nothing about it can be acted on any more.
  const isArchived = !!projectData?.isArchived;

  // What this one application released - 0 until it is approved.
  const releasedAmount = Number(fundDetails?.totalAmountReleased ?? 0);

  // "Total Amount Released By Admin" is a running total for the block, not for
  // this application alone: FA1 + FA2 + ... up to and including this FA. The
  // backend already computes it (`totalPaymentReleasedTillDate`, the same
  // figure the Fund Application list's Total Balance column is built from), so
  // it is read from there rather than recomputed. Legacy rows with no FA year
  // fall outside that running total, so this FA's own release is the floor.
  const cumulativeReleasedAmount = Math.max(
    Number(projectData?.totalPaymentReleasedTillDate ?? 0),
    releasedAmount
  );

  // Balance is Eco-Restoration Amount minus everything released so far, so it
  // is derived here rather than read from the stored `amountLeft`, which only
  // ever netted off this FA's own release.
  const balanceAmount =
    Number(landOwnership?.demandNote ?? 0) - cumulativeReleasedAmount;

  const hasUcOnRecord =
    !!projectData?.utilizationCertificateDocUrl ||
    fundDetails?.utilizedAmount != null;

  // This FA's own certificate, once one exists (uploaded, accepted, or sent
  // back for correction), is always shown read-only — the previous year's UC
  // covers the gap before that (below). Before it exists, the admin also has
  // the option to record it directly once the fund has been released
  // (Fund Released / Payment Receipt Uploaded) — self-approving on submit,
  // the same as verifying one the SNO uploaded.
  const ucPanelMode: "upload" | "view" | null = hasUcOnRecord
    ? "view"
    : (status === "APPROVED" || status === "PAYMENT_ACKNOWLEDGED") &&
      !isViewOnly &&
      !isArchived
    ? "upload"
    : null;

  // The previous year's UC is context for releasing this FA's fund. Once this
  // FA has its own UC on record, that becomes the subject and the prior
  // year's drops off the page.
  const previousUc = previousUcFromCurrentFa(projectData);

  const showPreviousUc = hasPreviousUcDetails(previousUc) && !hasUcOnRecord;

  const hasDocuments =
    projectData?.previousUtilizationCertificateDocUrl ||
    projectData?.previousRevalidationLetterDocUrl ||
    projectData?.paymentApplicationDocUrl ||
    projectData?.applicationApproveDocUrl ||
    projectData?.paymentAcknowledgementDocUrl;

  // Shared by the loaded page and its skeleton, so the shell around them is
  // identical and only the body swaps once the data lands.
  const pageSurfaceStyles = {
    children: {
      marginTop: "20px",
      padding: { xs: "16px", md: "22px" },
      paddingBottom: "40px",
      borderRadius: "18px",
      backgroundColor: "#F5FAF8",
      "@media print": { backgroundColor: "#FFFFFF", padding: 0 },
    },
  };

  return (
    <Box>
      {/* A status change lands by refetching in place — this is the only sign
          that the page is still catching up with the action just taken. */}
      <PageProgressBar show={isFetching && !isLoading} />
      {!isLoading && !!fundDetails ? (
        <DashboardWrapper
          title="Fund Application Details"
          customStyles={pageSurfaceStyles}
          buttonText="Print"
          buttonIcon="/assets/svg/export.svg"
          onButtonClick={handlePrint}
          showBackBtn
        >
          <Box ref={componentRef} id="printable-content">
            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "16px",
                }}
              >
                <Box
                  sx={{
                    fontSize: "22px",
                    fontWeight: 600,
                    color: fundUi.value,
                  }}
                >
                  Fund Application No : {fundDetails?.id}
                </Box>
                <StatusPill status={status} />
                {isArchived && (
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      padding: "6px 14px",
                      borderRadius: "999px",
                      backgroundColor: "#F1F3F2",
                      color: "#5A6b66",
                      fontSize: "13px",
                      fontWeight: 600,
                      lineHeight: 1.2,
                    }}
                  >
                    {projectData?.archivedAt
                      ? `Archived on ${shortDate(projectData.archivedAt)}`
                      : "Archived"}
                  </Box>
                )}
              </Box>
              {!!installmentLabel(fundDetails) && (
                <Box
                  sx={{
                    fontSize: "14px",
                    color: fundUi.label,
                    marginTop: "6px",
                  }}
                >
                  {installmentLabel(fundDetails)}
                </Box>
              )}
            </Box>

            <DetailLayout>
              <SectionCard
                title="Application Timeline"
                icon={<AccessTimeIcon />}
                fullWidth
              >
                <ApplicationTimeline fund={fundDetails} />

                {/* The audit trail on the left, the exact hand-off timestamps
                    in the corner opposite it. */}
                <SubSectionRow>
                  <SubSection title="Status History" divider={false}>
                    <StatusHistoryTrail
                      statusHistory={projectData?.statusHistory}
                    />
                  </SubSection>
                  {(fundDetails?.raisedDate || fundDetails?.releasedDate) && (
                    <SubSection title="Key Dates" align="right" divider={false}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "14px",
                        }}
                      >
                        {fundDetails?.raisedDate && (
                          <DetailField
                            label="Fund Application Raised to Admin"
                            value={dateAndTimeStamp(fundDetails?.raisedDate)}
                          />
                        )}
                        {fundDetails?.releasedDate && (
                          <DetailField
                            label="Fund Released"
                            value={dateAndTimeStamp(fundDetails?.releasedDate)}
                          />
                        )}
                      </Box>
                    </SubSection>
                  )}
                </SubSectionRow>
              </SectionCard>

              <SectionCard
                title="Eco-Restoration Blocks Details"
                icon={<ParkOutlinedIcon />}
                accent
              >
                <DetailFieldGrid columns={2}>
                  <DetailField
                    label="Entity Name"
                    value={landOwnership?.user?.firstName}
                    span
                  />
                  <DetailField
                    label="Cost Estimate ID"
                    value={
                      <Box
                        component={RouterLink}
                        to={`${path.landOwnershipDetails}?${createSearchParams({
                          id: fundDetails?.landOwnershipEpid ?? "",
                        })}`}
                        sx={{
                          color: fundUi.iconGreen,
                          textDecoration: "none",
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        {fundDetails?.landOwnershipEpid}
                      </Box>
                    }
                  />
                  <DetailField
                    label="State"
                    value={
                      landOwnership?.projectDetail?.address?.stateDetail?.name
                    }
                  />
                  <DetailField
                    label="Land Registration No."
                    value={landOwnership?.projectDetail?.registration_no}
                  />
                  <DetailField
                    label="District"
                    value={
                      landOwnership?.projectDetail?.address?.districtDetail
                        ?.name
                    }
                  />
                  <DetailField
                    label="Eco-Restoration Amount"
                    value={
                      landOwnership?.demandNote
                        ? money(landOwnership?.demandNote)
                        : undefined
                    }
                  />
                  <DetailField
                    label="Division"
                    value={
                      landOwnership?.projectDetail?.landSpecification?.division
                    }
                  />
                  <DetailField
                    label="Net Planned Area (Hectares)"
                    value={landOwnership?.netPlantationArea}
                  />
                  <DetailField
                    label="Allocated Division"
                    value={
                      landOwnership?.projectDetail
                        ?.organizationAffiliationDetail?.userDetail?.division
                    }
                  />
                  <DetailField
                    label="Land Area Requested (Hectares)"
                    value={
                      Number.isFinite(totalArea)
                        ? totalArea.toFixed(2)
                        : undefined
                    }
                  />
                </DetailFieldGrid>
              </SectionCard>

              {/* Estimated Fund Details - hidden, the SNO no longer fills
              these in on the Raise Fund Application form (see
              RaiseFundApplication), so they now always read 0.
              <SectionCard
                title="Estimated Fund Details"
                icon={<InsightsOutlinedIcon />}
              >
                <AmountRow
                  label="Estimated Advance Work Cost"
                  value={money(fundDetails?.estimatedAdvanceWorkCost)}
                />
                <AmountRow
                  label="Estimated Raising/Creation Cost"
                  value={money(fundDetails?.estimatedRaisingCost)}
                />
                <AmountRow
                  label="Estimated Maintenance Cost"
                  value={money(fundDetails?.estimatedMaintenanceCost)}
                />
                <AmountRow
                  label="Estimated Monitoring Cost"
                  value={money(fundDetails?.estimatedMonitoringCost)}
                />
                <AmountRow
                  label="Estimated Contingency Cost"
                  value={money(fundDetails?.estimatedContingencyCost)}
                />
                <AmountRow
                  label="Total Estimated Cost"
                  value={money(fundDetails?.totalEstimatedCost)}
                  tone="total"
                />
              </SectionCard> */}

              <SectionCard title="Requested Fund" icon={<CurrencyRupeeIcon />}>
                <AmountRow
                  label="Advance Work Cost"
                  value={money(fundDetails?.advanceWorkCost)}
                />
                <AmountRow
                  label="Raising/Creation Cost"
                  value={money(fundDetails?.raisingCost)}
                />
                <AmountRow
                  label="Maintenance Cost"
                  value={money(fundDetails?.maintenanceCost)}
                />
                <AmountRow
                  label="Monitoring Cost"
                  value={money(fundDetails?.monitoringCost)}
                />
                <AmountRow
                  label="Contingency Cost"
                  value={money(fundDetails?.contingencyCost)}
                />
                <AmountRow
                  label="Total Amount Requested"
                  value={money(fundDetails?.totalCost)}
                  tone="total"
                />
                <AmountRow
                  label="Total Amount Released By Admin"
                  value={money(cumulativeReleasedAmount)}
                  tone="soft"
                />
                <AmountRow
                  label="Balance Amount"
                  value={money(balanceAmount)}
                  tone="strong"
                />
              </SectionCard>

              <SectionCard
                title="Additional Information From IO"
                icon={<InfoOutlinedIcon />}
              >
                <DetailFieldGrid columns={1}>
                  <DetailField
                    label="Additional Information"
                    value={fundDetails?.additionalInfo}
                  />
                </DetailFieldGrid>
              </SectionCard>

              {projectData?.paymentApplicationDocUrl && (
                <SectionCard
                  title="Signed Fund Application from SNO"
                  icon={<DescriptionOutlinedIcon />}
                >
                  <DocumentTile
                    fileUrl={projectData?.paymentApplicationDocUrl}
                    meta={
                      fundDetails?.raisedDate
                        ? `Uploaded on ${shortDate(fundDetails?.raisedDate)}`
                        : undefined
                    }
                  />
                  <Box sx={{ marginTop: "14px" }}>
                    <DetailField
                      label="SNO Remark"
                      value={fundDetails?.snoRemark}
                    />
                  </Box>
                </SectionCard>
              )}

              {projectData?.applicationApproveDocUrl && (
                <SectionCard
                  title="Fund Released Order from Admin"
                  icon={<DescriptionOutlinedIcon />}
                >
                  <DocumentTile
                    fileUrl={projectData?.applicationApproveDocUrl}
                    meta={
                      fundDetails?.releasedDate
                        ? `Uploaded on ${shortDate(fundDetails?.releasedDate)}`
                        : undefined
                    }
                  />
                  {fundDetails?.remark && (
                    <Box sx={{ marginTop: "14px" }}>
                      <DetailField
                        label="Admin Remark"
                        value={fundDetails?.remark}
                      />
                    </Box>
                  )}
                  <Box sx={{ marginTop: "14px" }}>
                    <DetailFieldGrid columns={2}>
                      {/* This release order's own amount. The Requested Fund
                      card above carries the running total across every FA of
                      this block, so this one is labelled for this application
                      to keep the two apart. */}
                      <DetailField
                        label="Amount Released In This Application"
                        value={
                          fundDetails?.totalAmountReleased != null
                            ? money(fundDetails?.totalAmountReleased)
                            : undefined
                        }
                      />
                      <DetailField
                        label="Fund Released Date"
                        value={
                          fundDetails?.releasedDate
                            ? shortDate(fundDetails?.releasedDate)
                            : undefined
                        }
                      />
                    </DetailFieldGrid>
                  </Box>
                </SectionCard>
              )}

              {projectData?.paymentAcknowledgementDocUrl && (
                <SectionCard
                  title="Payment Receipt (As Per G.A.R. 6)"
                  icon={<ReceiptLongOutlinedIcon />}
                >
                  <DocumentTile
                    fileUrl={projectData?.paymentAcknowledgementDocUrl}
                    meta={
                      fundDetails?.paymentAcknowledgementDate
                        ? `Uploaded on ${shortDate(
                            fundDetails?.paymentAcknowledgementDate
                          )}`
                        : undefined
                    }
                  />
                </SectionCard>
              )}

              {showPreviousUc && (
                <SectionCard
                  title="Previous UC"
                  icon={<DescriptionOutlinedIcon />}
                  fullWidth
                >
                  <PreviousUcSummary uc={previousUc} />
                </SectionCard>
              )}

              {!hasDocuments && (
                <SectionCard
                  title="Documents"
                  icon={<DescriptionOutlinedIcon />}
                >
                  <Box sx={{ fontSize: "13px", color: fundUi.label }}>
                    No documents have been uploaded against this application
                    yet.
                  </Box>
                </SectionCard>
              )}

              {fundDetails?.queries?.length > 0 && (
                <SectionCard
                  title="Fund Transfer Query"
                  icon={<ForumOutlinedIcon />}
                  fullWidth
                >
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        md: "repeat(2, minmax(0, 1fr))",
                      },
                      gap: "12px",
                    }}
                  >
                    {fundDetails?.queries.map((item) => (
                      <Box
                        key={item?.id ?? item?.created_at}
                        sx={{
                          padding: "12px 14px",
                          borderRadius: "12px",
                          border: `1px solid ${fundUi.border}`,
                          backgroundColor: "#FBFDFC",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: "10px",
                            fontSize: "12px",
                            color: fundUi.label,
                            marginBottom: "6px",
                          }}
                        >
                          <Box sx={{ fontWeight: 600 }}>
                            {item?.user?.firstName}
                          </Box>
                          <Box>
                            {dateAndTimeStamp(item?.created_at, "Asia/Kolkata")}
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            fontSize: "13px",
                            color: fundUi.value,
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {item?.query}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </SectionCard>
              )}
            </DetailLayout>
          </Box>

          {ucPanelMode && (
            <ActionPanel
              id={FUND_SECTIONS.uc}
              highlighted={targetSection === FUND_SECTIONS.uc}
              busy={isSubmittingUc}
              title={
                ucPanelMode === "view"
                  ? "Utilization Certificate"
                  : "Upload Utilization Certificate"
              }
              icon={<DescriptionOutlinedIcon />}
              description={
                ucPanelMode === "view"
                  ? "The utilization certificate recorded against this fund application."
                  : "Record the audited utilization against the fund already released, and attach the certificate."
              }
            >
              <UtilizationCertificatePanel
                data={projectData}
                ecoRestorationCost={landOwnership?.demandNote}
                mode={ucPanelMode}
                isSubmitting={isSubmittingUc}
                enforcePreviousUcGate={false}
                onSubmit={(payload) =>
                  setPendingAction({
                    title: "Record this utilization certificate?",
                    message: `${money(
                      payload.utilizedAmount
                    )} will be recorded as utilized against ${
                      fundDetails?.id
                    }, and the certificate marked as accepted.`,
                    confirmText: "Submit UC",
                    onConfirm: () => submitUc(payload),
                  })
                }
              />
            </ActionPanel>
          )}

          {status === "UC_UPLOADED" && !isViewOnly && !isArchived && (
            <ActionPanel
              id={FUND_SECTIONS.verifyUc}
              highlighted={targetSection === FUND_SECTIONS.verifyUc}
              title="Verify Utilization Certificate"
              busy={isDeciding}
              icon={<DescriptionOutlinedIcon />}
              description="Check the uploaded utilization certificate above, then record your remark and accept or reject it."
            >
              <FormGrid>
                <Input
                  name="remarks"
                  label="Remarks"
                  placeholder="Add a remark for this decision"
                  control={control}
                  errors={errors}
                  multiline
                  customStyles={{
                    input: {
                      height: "auto",
                      fontFamily: "Poppins",
                    },
                  }}
                />
              </FormGrid>
              <ActionFooter>
                <Button
                  text="Reject UC"
                  variant="outlined"
                  color="error"
                  onClick={() =>
                    setPendingAction({
                      title: "Reject this utilization certificate?",
                      message:
                        "The certificate goes back to the SNO to be corrected and re-uploaded. Your remark is recorded against the application.",
                      confirmText: "Reject UC",
                      tone: "danger",
                      onConfirm: () =>
                        mutate({
                          FundApplicationId,
                          data: {
                            status: "UC_REJECTED",
                            remark: watch("remarks"),
                          },
                        }),
                    })
                  }
                  customStyles={{
                    root: {
                      fontSize: "16px",
                      color: "error.main",
                      borderColor: "error.main",
                    },
                  }}
                />
                <Button
                  text="Approve UC"
                  type="submit"
                  onClick={() =>
                    setPendingAction({
                      title: "Accept this utilization certificate?",
                      message:
                        "The utilization certificate will be marked as accepted for this fund application. This cannot be undone from here.",
                      confirmText: "Approve UC",
                      onConfirm: () =>
                        mutate({
                          FundApplicationId,
                          data: {
                            status: "UC_APPROVED",
                            remark: watch("remarks"),
                          },
                        }),
                    })
                  }
                  customStyles={{
                    root: {
                      fontSize: "16px",
                    },
                  }}
                />
              </ActionFooter>
            </ActionPanel>
          )}

          {status === "RAISED" && !isViewOnly && !isArchived && (
            <ActionPanel
              id={FUND_SECTIONS.release}
              highlighted={targetSection === FUND_SECTIONS.release}
              title="Release Fund"
              busy={isDeciding || isRaisingQuery}
              icon={<CurrencyRupeeIcon />}
              description={`Requested ${money(
                fundDetails?.totalCost
              )} against an Eco-Restoration Amount of ${money(
                landOwnership?.demandNote
              )}. Enter the amount and date you are releasing it, attach the signed release letter, then release the fund.`}
            >
              <FormGrid>
                <Box>
                  <Input
                    name="totalAmountReleased"
                    label="Total Amount Released BY Admin"
                    placeholder="Enter Total Amount Released BY Admin"
                    control={control}
                    rules={{ required: errorMessage.required }}
                    errors={errors}
                    height="52px"
                    type="number"
                  />
                  <Box sx={{ marginTop: "20px" }}>
                    <DateInput
                      name="releasedDate"
                      label="Fund Released Date"
                      control={control}
                      errors={errors}
                      minAge={0}
                      rules={{ required: errorMessage.required }}
                      clearErrors={clearErrors}
                    />
                  </Box>
                  <Box sx={{ marginTop: "20px" }}>
                    <FormHint
                      label="Download Fund Release Letter"
                      description="The bilingual sanction letter, pre-filled from this application — installment, financial year, amount in figures and words, parcel and entity. Enter the amount above first, then choose PDF for a fixed copy or DOCX for editing and e-signing. Upload the signed copy alongside."
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "12px",
                        }}
                      >
                        <Button
                          text="Download PDF"
                          variant="outlined"
                          loading={buildingLetterFormat === "pdf"}
                          disabled={buildingLetterFormat !== null}
                          onClick={() => handleDownloadReleaseLetter("pdf")}
                        />
                        <Button
                          text="Download DOCX"
                          variant="outlined"
                          loading={buildingLetterFormat === "docx"}
                          disabled={buildingLetterFormat !== null}
                          onClick={() => handleDownloadReleaseLetter("docx")}
                        />
                      </Box>
                    </FormHint>
                  </Box>
                </Box>
                <FileUpload
                  name="paymentApproveDocId"
                  label="Upload Fund Release Letter"
                  control={control}
                  errors={errors}
                  setValue={setValue}
                  getValues={getValues}
                  setError={setError}
                  clearErrors={clearErrors}
                  maxLimit={1}
                  multiple={true}
                  customStyles={{
                    label: {
                      whiteSpace: "wrap",
                    },
                  }}
                  supportedFormatMessage="Upload Fund Released Letter"
                  trigger={trigger}
                  required={true}
                  rules={{
                    validate: {
                      isLength: (value) => {
                        return value && value["length"] >= 1;
                      },
                    },
                  }}
                  dragDropHeading=""
                />
                <Input
                  name="remarks"
                  label="Remarks"
                  placeholder="Add a remark for this decision"
                  control={control}
                  errors={errors}
                  multiline
                  customStyles={{
                    input: {
                      height: "auto",
                      fontFamily: "Poppins",
                    },
                  }}
                />
                <Input
                  name="query"
                  label="Query"
                  placeholder="Ask the SNO for clarification instead of deciding now"
                  control={control}
                  errors={errors}
                  multiline
                  customStyles={{
                    input: {
                      height: "auto",
                      fontFamily: "Poppins",
                    },
                  }}
                />
              </FormGrid>
              <ActionFooter
                leftAction={
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      gap: "6px",
                    }}
                  >
                    <Button
                      text="Raise Query"
                      variant="outlined"
                      onClick={() =>
                        setPendingAction({
                          title: "Send this query to the SNO?",
                          message:
                            "The application goes back to the SNO with your query and stays out of your queue until they reply.",
                          confirmText: "Raise Query",
                          onConfirm: () =>
                            mutateQueryData({
                              id: FundApplicationId,
                              query: watch("query"),
                            }),
                        })
                      }
                      disabled={!watch("query")}
                      customStyles={queryButtonStyles}
                    />
                    {!watch("query") && (
                      <ActionNote>
                        Fill the Query field above to send this back to the SNO.
                      </ActionNote>
                    )}
                  </Box>
                }
              >
                <Button
                  text="Reject"
                  variant="outlined"
                  color="error"
                  onClick={() =>
                    setPendingAction({
                      title: "Reject this fund application?",
                      message:
                        "No fund will be released and the application is closed as rejected. This cannot be undone from here.",
                      confirmText: "Reject Application",
                      tone: "danger",
                      onConfirm: () =>
                        mutate({
                          FundApplicationId,
                          data: {
                            status: "REJECTED",
                            remark: watch("remarks"),
                          },
                        }),
                    })
                  }
                  customStyles={{
                    root: {
                      fontSize: "18px",
                      color: "error.main",
                      borderColor: "error.main",
                    },
                  }}
                />
                <Button
                  text="Release Fund"
                  type="submit"
                  disabled={!isValid}
                  onClick={() =>
                    setPendingAction({
                      title: "Release fund for this application?",
                      message: `${money(
                        watch("totalAmountReleased")
                      )} will be released against ${
                        fundDetails?.id
                      } as of ${shortDate(
                        watch("releasedDate")
                      )}, leaving a balance of ${money(
                        Number(landOwnership?.demandNote ?? 0) -
                          (cumulativeReleasedAmount +
                            Number(watch("totalAmountReleased") ?? 0))
                      )}. This cannot be undone from here.`,
                      confirmText: "Release Fund",
                      onConfirm: () =>
                        mutate({
                          FundApplicationId,
                          data: {
                            status: "APPROVED",
                            remark: watch("remarks"),
                            totalAmountReleased: Number(
                              watch("totalAmountReleased")
                            ),
                            paymentApproveDocId: docId[0]?.key,
                            releasedDate: watch("releasedDate"),
                          },
                        }),
                    })
                  }
                  customStyles={{
                    root: {
                      fontSize: "16px",
                    },
                  }}
                />
              </ActionFooter>
            </ActionPanel>
          )}

          <ConfirmActionDialog
            action={pendingAction}
            onClose={() => setPendingAction(null)}
            isProcessing={isActionRunning}
          />
        </DashboardWrapper>
      ) : isLoading ? (
        // Same shell as the loaded page, so the header and surface are already
        // in place and only the cards fill in.
        <DashboardWrapper
          title="Fund Application Details"
          customStyles={pageSurfaceStyles}
          showBackBtn
        >
          <FundDetailSkeleton />
        </DashboardWrapper>
      ) : (
        <Box {...styles("loading")}>
          {/* Once the fetch has settled with nothing to show, this stops on a
              message with a way out instead of spinning indefinitely. */}
          <FundLoadError onRetry={() => refetch()} />
        </Box>
      )}

      <Snackbar
        open={!!feedback}
        message={feedback?.message ?? ""}
        severity={feedback?.severity ?? "success"}
        resetSnackbar={() => setFeedback(null)}
        duration={4000}
      />
    </Box>
  );
};

export default FundApplicationDetails;
